import React from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import LazyLoad from 'react-lazyload';
import { Box, Container, Typography, Grid } from '@mui/material';
import { useStyles } from './styles';
import CustomButton from 'components/CustomButton';
import ProfessionalScientific from '../ProfessionalScientific';
import Link from 'next/link';
import TypeOfScience from 'components/TypeOfScience';
import useIsMobile from 'hooks/useIsMobile';

const DynamicFaq = dynamic(() => import('../Faq'));
const DynamicHowItWork = dynamic(() => import('components/HowItWork'));
const DynamicTopInstitutions = dynamic(() => import('components/TopInstitutions'));
const DynamicGlobalTeam = dynamic(() => import('components/GlobalTeam'));

export default function Types() {
  const classes = useStyles();
  const isMobile = useIsMobile();

  return (
    <>
      <Head>
        <title>Custom Services - Mind The Graph</title>

        <meta
          key="og:title"
          name="og:title"
          property="og:title"
          content="Custom Services - Mind The Graph"
        />

        <meta
          key="description"
          name="description"
          content="A free infographic maker & creator for medical doctors and scientists. Exclusive scientific illustrations, graphs and infographics for your papers, classes and graphical abstracts."
        />

        <meta
          key="og:description"
          name="og:description"
          property="og:description"
          content="A free infographic maker & creator for medical doctors and scientists. Exclusive scientific illustrations, graphs and infographics for your papers, classes and graphical abstracts."
        />
      </Head>

      <Box pt={6} pb={7}>
        <Container style={{ maxWidth: 1150 }}>
          <LazyLoad offset={100}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box maxWidth={475}>
                  <Typography variant="h1" className={classes.title}>
                    Are you looking for <b>on-demand figures and infographics</b> to communicate
                    science?
                  </Typography>

                  <Typography className={classes.heroParagraph}>
                    <b>We are specialized</b> in Graphical Abstracts, Science Figures all curated by
                    scientists.
                  </Typography>

                  <Typography className={classes.heroParagraph}>
                    <b>We follow the high standards</b> of scientific publications, ensuring your
                    work makes an impact on your community
                  </Typography>

                  <Typography className={classes.heroParagraph}>
                    <b>Let us handle the visual</b> representation while you focus on your research.
                  </Typography>

                  <Box className={classes.containerButtonHero}>
                    <Link href={!isMobile ? '#select-type' : '/unsupported-device'}>
                      <CustomButton>Request your design</CustomButton>
                    </Link>

                    <Box className={classes.customersHero}>
                      <Image
                        src="/images/people-customers.png"
                        width={82}
                        height={23.3}
                        alt="People Customers"
                        loading="eager"
                      />

                      <span>
                        <b>20k</b> Customers
                      </span>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Box zIndex={1} style={{ position: 'relative' }}>
                    <Image
                      src="/images/pharmaceutical-infographic-ab.png"
                      width={524}
                      height={472}
                      alt="Pharmaceutical infographics"
                      priority
                    />
                  </Box>

                  <Box className={classes.moonIcon}>
                    <Image
                      src="/images/background-graphics/moon-full.svg"
                      alt="moon image"
                      width={118}
                      height={118}
                      loading="eager"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </LazyLoad>
        </Container>
      </Box>

      <Box sx={{ display: { md: 'block', xs: 'none' } }}>
        <TypeOfScience />
      </Box>

      <Box sx={{ display: { md: 'block', xs: 'none' } }}>
        <DynamicHowItWork />
      </Box>

      <DynamicTopInstitutions />

      <DynamicGlobalTeam />

      <Box py={7}>
        <DynamicFaq />
      </Box>

      <ProfessionalScientific />
    </>
  );
}
